// ====================================================
// src/components/block/liveStreamManagement/videoEffects/AvatarModels.ts
//
// Registre des modèles 3D disponibles pour l'effet "Avatar" (cf. AvatarEffect.tsx,
// AvatarRenderer.ts) — même principe que les autres registry du projet (enchaînements,
// effets audio/vidéo) : ajouter un modèle, c'est ajouter une entrée ici, rien d'autre.
//
// `targetRatio` : taille visée à l'écran pour CE modèle, en multiples de sa référence
// d'échelle — la distance inter-yeux pour un ancrage 'face'/'eyes'/'aboveHead', la
// largeur de paume pour un ancrage 'hand' (cf. AvatarRenderer, updateFaceSlotTransform/
// updateHandSlotTransform) — chaque modèle a des proportions différentes, un ratio
// commun ferait paraître l'un minuscule et l'autre énorme relativement à son ancrage.
// Centrage et
// taille propre du maillage (nativeSize) ne sont PAS mesurés à la main : recalculés
// automatiquement à partir de sa boîte englobante au chargement (cf. AvatarRenderer.ts).
//
// `anchor` : à quel repère du visage ce modèle s'accroche — une créature "posée sur
// le visage" (poisson, renard) et un chapeau qui doit flotter AU-DESSUS de la tête
// n'ont pas le même point d'ancrage ni le même mode de centrage (cf. AvatarRenderer,
// computeAnchorPoint/instantiate) :
//   - 'face'      : entre les sourcils — le défaut, pour une créature sur le visage.
//   - 'eyes'      : milieu des deux yeux — pour des lunettes.
//   - 'aboveHead' : point extrapolé au-dessus du front (MediaPipe n'a aucun repère sur
//                   le crâne/cheveux, seulement sur le visage) — pour un chapeau/couronne.
//   - 'hand'      : centre d'UNE main détectée (cf. HandTracker.ts) — pour un accessoire
//                   porté à la main (bague...). Contrairement aux trois précédents, ce
//                   n'est pas un repère du visage : une instance par main détectée, pas
//                   par visage (cf. AvatarRenderer, needsHandTracking).
//
// `url` (modèle glTF distant) OU `build` (généré en code) — jamais les deux. `build`
// sert aux accessoires simples (couronne, lunettes, bague) : une primitive Three.js
// suffit, pas besoin de dépendre d'un fichier externe (licence, poids, disponibilité)
// pour une forme aussi simple — cf. AvatarProceduralModels.ts.
//
// Expressions (clignement, ouverture de bouche, sourire...) : AUCUN champ à déclarer
// ici — AvatarRenderer détecte tout seul, à l'instanciation, si le maillage chargé
// porte des morph targets (cf. LoadedModel.morphTargets dans AvatarRenderer.ts) et
// les anime alors depuis les blendshapes MediaPipe du visage suivi. Un modèle sans
// morph targets (poisson, renard, accessoires...) ne fait simplement rien de plus.
// ====================================================

import * as THREE from 'three';
import { HandRigParts } from './AvatarProceduralModels';
// Constructeurs des accessoires générés en code, retirés du registre (voir plus bas) :
// import { buildCrown, buildGlasses, buildRing, buildMoustache, buildWig, buildHandRigParts } from './AvatarProceduralModels';

export type AvatarAnchorKind = 'face' | 'eyes' | 'aboveHead' | 'hand';

// Visage/mains/décorations sont TROIS familles INDÉPENDANTES qui peuvent tourner en
// même temps (cf. AvatarEffect) : "Visage" et "Mains" restent un choix UNIQUE chacun
// (remplacent le visage/la main par un modèle), "Décoration" est un ensemble
// CUMULABLE (plusieurs accessoires à la fois, cf. AvatarModelDef.isDecoration) posé
// par-dessus, quel que soit le modèle "Visage"/"Mains" choisi (ou aucun). Cette
// fonction dit si un ancrage donné relève de la famille "Mains", pour filtrer
// AVATAR_MODELS dans chaque sélecteur.
export function isHandAnchor(anchor: AvatarAnchorKind): boolean {
  return anchor === 'hand';
}

/**
 * Réglage FIN d'UNE décoration, relatif au point d'ancrage (visage/main) suivi —
 * réglé visuellement via le panneau de calibration (cf. DecorationCalibrationModal,
 * icône ⚙ sur chaque décoration) plutôt que deviné à l'aveugle : tête/main de
 * référence vue de face/dessus/profil, la décoration positionnée dessus, curseurs
 * pour ajuster. Une fois réglé, ENREGISTRÉ dans AvatarConfig.decorationTransforms
 * (pas ici — ceci n'est que le point de départ par défaut, cf. AvatarModelDef.
 * defaultTransform) et utilisé tel quel en régie réelle par AvatarRenderer.
 */
export interface DecorationTransform {
  /** Décalage LOCAL par rapport au point d'ancrage, en multiples de la distance
   * inter-yeux (visage) ou de la largeur de paume (main) — x: droite, y: haut,
   * z: vers la caméra (donc "devant" le visage/la main). Tourne AVEC le visage/la
   * main suivi(e) (cf. AvatarRenderer), comme s'il était réellement attaché dessus. */
  position: [number, number, number];
  /** Rotation LOCALE supplémentaire (radians), par-dessus l'orientation suivie —
   * autour des mêmes axes x/y/z que `position`, ancrés sur la tête/main suivie
   * (donc AVEC son orientation, pas ceux de l'écran). */
  rotation: [number, number, number];
  /** Homothétie LOCALE par axe (x/y/z séparément, pas un facteur unique) — multiplie
   * la taille par défaut de la décoration (targetRatio) sur CHAQUE axe, 1 = défaut.
   * Permet d'étirer/aplatir une décoration (ex. élargir une couronne sans l'épaissir)
   * plutôt qu'un unique curseur "Échelle" qui grossit tout uniformément. */
  scale: [number, number, number];
  /** Demi-profondeur (axe Z, en multiples de distance inter-yeux) entre le point
   * RÉELLEMENT suivi par MediaPipe (racine du nez, seul repère de profondeur qui
   * existe en régie réelle) et le CENTRE DU VOLUME de la tête/main — sert à décaler
   * l'ancrage réel de ce plan vers ce centre, pour que position.z=0 représente "le
   * centre" aussi bien ici (repère du panneau, toujours centré) qu'en régie (cf.
   * AvatarRenderer.updateFaceSlotTransform/updateHandSlotTransform). RÉGLÉ AU CURSEUR
   * dans DecorationCalibrationModal ("Profondeur") — une mesure automatique sur le
   * modèle de référence affiché a été essayée, mais retombe sur une approximation
   * trop plate (aucun modèle 3D disponible ne représente fidèlement la profondeur
   * réelle d'un crâne humain, cheveux/boîte crânienne compris) : livré à l'ajustement
   * de l'utilisateur, qui peut voir le résultat réel en régie — pas moi. */
  centerDepthOffset: number;
}

export const DEFAULT_DECORATION_TRANSFORM: DecorationTransform = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
  // ≈ demi-profondeur anthropométrique réaliste (profondeur de tête ≈ 19-20cm ÷ écart
  // inter-yeux ≈ 6,3cm ÷ 2 ≈ 1,5) — un point de départ, pas une valeur exacte : à
  // ajuster au curseur "Profondeur" du panneau de calibration en se voyant en régie.
  centerDepthOffset: 1.5,
};

export interface AvatarModelDef {
  id: string;
  label: string;
  icon: 'fish' | 'crown' | 'glasses' | 'ring' | 'face' | 'hand' | 'generic';
  targetRatio: number;
  anchor: AvatarAnchorKind;
  /** Pour un modèle de main : le côté de la personne auquel il est réservé (choisi à
   * l'ajout). Absent = utilisable des deux côtés (modèles d'avant la distinction). */
  handSide?: 'left' | 'right';
  /** Accessoire cumulable (cf. AvatarEffect, section "Décoration") plutôt qu'un
   * modèle qui REMPLACE le visage/la main — plusieurs décorations peuvent être
   * actives en même temps, entre elles ET avec un modèle "Visage"/"Mains" (cf.
   * AvatarRenderer, un jeu de slots dédié par décoration). Faux/absent pour un
   * modèle de remplacement (Poisson, Renard, Visage, les rigs de main...). */
  isDecoration?: boolean;
  /** Point de départ du réglage fin (cf. DecorationTransform) avant toute
   * calibration manuelle par l'utilisateur — cf. AvatarEffect.defaultConfig,
   * AvatarConfig.decorationTransforms (ce qui est RÉELLEMENT utilisé une fois
   * calibré, cette valeur-ci ne sert qu'à amorcer le panneau). Uniquement
   * pertinent si isDecoration. */
  defaultTransform?: DecorationTransform;
  /** Le long de l'axe caméra, à quel point de la boîte englobante ancrer ce modèle
   * (cf. AvatarRenderer, ModelTemplate.center/frontCenter) :
   *   - 'center' (défaut) : son centre volumétrique — convient à une créature posée
   *     SUR le visage (poisson, renard), fine ou dont la profondeur ne se remarque pas.
   *   - 'front' : sa face la plus proche de la caméra — pour un modèle qui REMPLACE le
   *     visage (une tête entière) : sinon son propre relief (nez, menton) ressort
   *     devant le vrai visage au lieu de s'y superposer (constaté avec "Visage"). */
  depthAnchor?: 'center' | 'front';
  url?: string;
  build?: () => THREE.Object3D;
  /** Rig "os + articulations" (cf. AvatarProceduralModels.buildHandRigParts),
   * repositionné/réorienté PAR PHALANGE chaque frame depuis les 21 repères de la
   * main suivie — remplace `build`/`url` (mutuellement exclusifs) pour un modèle qui
   * veut suivre les doigts individuellement plutôt qu'un seul bloc rigide (cf.
   * AvatarRenderer.updateHandRigTransform). Uniquement pour anchor: 'hand'. */
  handRigBuild?: () => HandRigParts;
}

// Plus aucun modèle en dur : chaque créateur téléverse les siens (.glb), rangés dans son
// workingSpace (cf. avatarModelStore.ts, qui remplit ce registre via setAvatarModels).
// Les anciennes entrées (poisson, renard, visage, anime, accessoires et rigs de main
// générés en code) sont conservées ci-dessous EN COMMENTAIRE, pour mémoire.
export const AVATAR_MODELS: AvatarModelDef[] = [];

/** Remplace le contenu du registre EN PLACE (les modules qui ont importé le tableau
 * gardent la même référence). Appelée par avatarModelStore.ts uniquement. */
export function setAvatarModels(models: AvatarModelDef[]): void {
  AVATAR_MODELS.splice(0, AVATAR_MODELS.length, ...models);
}

// const ANCIENS_MODELES: AvatarModelDef[] = [//
//   {
//     id: 'fish',
//     label: 'Poisson',
//     icon: 'fish',
//     targetRatio: 2.5,
//     anchor: 'face',
//     url: 'https://cdn.jsdelivr.net/gh/KhronosGroup/glTF-Sample-Assets@main/Models/BarramundiFish/glTF-Binary/BarramundiFish.glb',
//   },
//   {
//     id: 'fox',
//     label: 'Renard',
//     icon: 'generic',
//     targetRatio: 3.5,
//     anchor: 'face',
//     url: 'https://cdn.jsdelivr.net/gh/KhronosGroup/glTF-Sample-Assets@main/Models/Fox/glTF-Binary/Fox.glb',
//   },
//   {
//     id: 'crown',
//     label: 'Couronne',
//     icon: 'crown',
//     targetRatio: 2.2,
//     // 'aboveHead' documente l'INTENTION (une créature/accessoire qui flotterait au-dessus
//     // du crâne) mais n'est plus ce qui ancre RÉELLEMENT une décoration en régie — cf.
//     // AvatarRenderer.updateFaceSlotTransform, toute décoration ancre désormais à 'face'
//     // (racine du nez) pour coïncider avec le repère TOUJOURS centré sur le visage dans
//     // DecorationCalibrationModal ; c'est defaultTransform.position ci-dessous qui porte
//     // tout le décalage vers le haut du crâne (départ approximatif, à affiner au panneau ⚙).
//     anchor: 'aboveHead',
//     build: buildCrown,
//     isDecoration: true,
//     defaultTransform: { position: [0, 1.4, 0.1], rotation: [0, 0, 0], scale: [1, 1, 1], centerDepthOffset: 1.5 },
//   },
//   {
//     id: 'wig',
//     label: 'Perruque',
//     icon: 'generic',
//     // Même ancrage que la Couronne ('aboveHead', mais cf. sa note : n'ancre plus
//     // réellement, seulement documentaire) — silhouette stylisée (calotte + frange +
//     // mèches), pas des mèches individuelles rendues une par une (hors de portée en
//     // primitives Three.js).
//     targetRatio: 2.6,
//     anchor: 'aboveHead',
//     build: buildWig,
//     isDecoration: true,
//     defaultTransform: { position: [0, 1.3, 0.1], rotation: [0, 0, 0], scale: [1, 1, 1], centerDepthOffset: 1.5 },
//   },
//   {
//     id: 'glasses',
//     label: 'Lunettes',
//     icon: 'glasses',
//     targetRatio: 1.4,
//     // 'eyes' documente l'intention — même remarque que Couronne (cf. son commentaire) :
//     // ancre réellement à 'face' (racine du nez), déjà quasiment à la hauteur des yeux,
//     // donc [0,0,0] reste un départ correct.
//     anchor: 'eyes',
//     build: buildGlasses,
//     isDecoration: true,
//     defaultTransform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1], centerDepthOffset: 1.5 },
//   },
//   {
//     id: 'moustache',
//     label: 'Moustache',
//     icon: 'generic',
//     // Ancrage 'face' (nez-sourcils) — decalage vers le bas (cf. defaultTransform,
//     // position[1] négatif) pour tomber au niveau de la lèvre supérieure.
//     targetRatio: 1,
//     anchor: 'face',
//     build: buildMoustache,
//     isDecoration: true,
//     defaultTransform: { position: [0, -0.9, 0], rotation: [0, 0, 0], scale: [1, 1, 1], centerDepthOffset: 1.5 },
//   },
//   {
//     id: 'ring',
//     label: 'Bague',
//     icon: 'ring',
//     targetRatio: 1.1,
//     anchor: 'hand',
//     build: buildRing,
//     isDecoration: true,
//     // Demi-épaisseur d'une main (bien plus fine qu'une tête) — même logique que
//     // centerDepthOffset ci-dessus pour les décorations "visage", ajustable au même
//     // curseur "Profondeur" du panneau de calibration.
//     defaultTransform: { position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1], centerDepthOffset: 0.2 },
//   },
//   {
//     id: 'hand-rig-stick',
//     label: 'Main bâtons (test)',
//     icon: 'hand',
//     // Rig articulé (cf. AvatarProceduralModels.buildHandRigParts) — chaque phalange
//     // suit individuellement les 21 repères MediaPipe de CETTE main, gauche/droite
//     // distingués par couleur (cf. AvatarRenderer.updateHandRigTransform). targetRatio
//     // ne sert PAS ici (la taille vient directement des repères mesurés, pas d'une
//     // boîte englobante statique) — laissé à 1 par convention, ignoré par le rig.
//     targetRatio: 1,
//     anchor: 'hand',
//     handRigBuild: () => buildHandRigParts('stick'),
//   },
//   {
//     id: 'hand-rig-skeleton',
//     label: 'Squelette (test)',
//     icon: 'hand',
//     // Même rig/mécanisme que "Main bâtons" — juste des os plus fins et des
//     // articulations bien plus grosses (nœuds), teinte ivoire, façon vrai squelette.
//     targetRatio: 1,
//     anchor: 'hand',
//     handRigBuild: () => buildHandRigParts('skeleton'),
//   },
//   {
//     id: 'hand-rig-skin',
//     label: 'Main peau (test)',
//     icon: 'hand',
//     // Même rig/mécanisme — capsules arrondies (plus organiques que des cylindres),
//     // épaisseur dégressive base→bout du doigt, matière éclairée pour un peu de relief.
//     targetRatio: 1,
//     anchor: 'hand',
//     handRigBuild: () => buildHandRigParts('skin'),
//   },
//   {
//     id: 'face',
//     label: 'Visage',
//     icon: 'face',
//     // "Face Cap" — tête scannée à 52 morph targets ARKit (clignement, sourire,
//     // sourcils, bouche, langue...), utilisée telle quelle par l'exemple officiel
//     // three.js (examples/webgl_morphtargets_face.html) pour être pilotée par des
//     // scores de blendshapes — exactement notre cas avec MediaPipe FaceLandmarker
//     // (mêmes 52 noms ARKit, cf. AvatarRenderer.modelTargetNameToBlendshapeName).
//     // Fichier du dépôt mrdoob/three.js (licence MIT du dépôt, pas de mention
//     // contraire trouvée), figé sur le tag EXACT correspondant à notre `three` installé
//     // (r186 = 0.186.0, cf. package.json) — jamais "dev"/"master", pour ne pas suivre
//     // une branche qui pourrait remplacer/retirer le fichier sous nos pieds.
//     url: 'https://cdn.jsdelivr.net/gh/mrdoob/three.js@r186/examples/models/gltf/facecap.glb',
//     // Plus grande dimension du modèle (sa hauteur) visée à ~2,4× la distance
//     // inter-yeux — pas calibré sur un vrai visage, à ajuster au test.
//     targetRatio: 2.4,
//     anchor: 'face',
//     // Une tête entière a un vrai volume (contrairement au poisson/renard, fins) : sans
//     // ça, son centre se pose au niveau du vrai nez et son propre relief ressort devant
//     // le vrai visage ("l'avatar avancé par rapport au visage", constaté en régie).
//     depthAnchor: 'front',
//   },
//   {
//     id: 'anime',
//     label: 'Anime (test)',
//     icon: 'generic',
//     // Tête stylisée (toon shading, contours) — servie localement depuis public/models
//     // (cf. AvatarRenderer, aucune config particulière requise : pas de KTX2/meshopt,
//     // pas de morph targets/animation, un GLTFLoader nu suffit). Fichier fourni par
//     // Alex, licence Fab/Sketchfab PAS ENCORE vérifiée — id et libellé marqués "test"
//     // exprès : ne pas laisser ce modèle en l'état dans une build destinée aux
//     // utilisateurs finaux tant que ce n'est pas confirmé (cf. échange du 2026-09-13).
//     url: '/models/anime-head.glb',
//     // Premier réglage, non calibré (même principe que "Visage").
//     targetRatio: 2.4,
//     anchor: 'face',
//     depthAnchor: 'front',
//   },
//
// ];

// Hauteur EXACTE (bbox, axe Y) de la tête/main de référence GÉNÉRIQUE (cf.
// AvatarProceduralModels.buildReferenceHead/Hand — sphère Y∈[-1.05,1.05] ; main du
// poignet y=-0.6 au bout des doigts y=1.79), dont la distance inter-yeux/largeur de
// paume VAUT exactement 1 unité PAR CONSTRUCTION. Sert de RÈGLE COMMUNE pour convertir
// "unités calibrées (distance inter-yeux/largeur de paume)" ↔ "unités natives d'un
// modèle chargé" — À LA FOIS dans DecorationCalibrationModal (recalage visuel du
// modèle de référence affiché) ET dans AvatarRenderer (fusion réelle d'une décoration
// dans le modèle "Visage"/"Mains" actif, cf. attachDecorationToFaceModel/
// attachDecorationToHandBone). Exportée ici plutôt que dupliquée dans chaque fichier :
// deux définitions séparées avaient fini par diverger (l'une sur AvatarModelDef.
// targetRatio, jamais vérifié sur un vrai visage ; l'autre sur cette mesure-ci),
// rendant le panneau et la régie incohérents pour tout réglage à grand décalage
// (constaté : lunettes visiblement trop avancées en régie alors que centrées dans le
// panneau — invisible sur la couronne, dont le décalage concerné était bien plus
// petit). Une seule règle, partagée, élimine la divergence par construction.
export const GENERIC_HEAD_HEIGHT = 2.10;
export const GENERIC_HAND_HEIGHT = 2.39;

/** null = aucun modèle choisi pour cette famille (visage/mains sont indépendants et
 * cumulables — cf. AvatarEffect, AvatarConfig.faceModelId/handModelId — chacun peut
 * être désactivé sans l'autre). */
export function getAvatarModelDef(id: string | null): AvatarModelDef | null {
  if (!id) return null;
  return AVATAR_MODELS.find(m => m.id === id) ?? null;
}
